import { isSameYear, isToday, isValid, isYesterday, parseISO } from 'date-fns';
import { format, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import i18n, { t } from 'i18next';

import { getDateFnsCurrentLocale } from '../i18n/I18n';

export function isValidDate(date) {
    return date instanceof Date && isValid(date);
}

/**
 * Normalize any input into a valid `Date`, or `null` when the input is
 * missing, of an unsupported type, or represents an invalid date.
 *
 * Accepts `Date` instances, ISO strings and numeric timestamps. This is the
 * single defensive entry point all formatters funnel through so that an
 * `Invalid Date` is never handed to date-fns `format()` (which throws).
 *
 * @param {unknown} input
 * @returns {Date | null}
 */
function toValidDate(input) {
    // Note : also avoid timestamp 0 : 1/1/1970
    if (!input) {
        return null;
    }
    if (input instanceof Date) {
        return isValid(input) ? input : null;
    }
    if (typeof input === 'string') {
        const parsed = parseISO(input);
        return isValid(parsed) ? parsed : null;
    }
    if (typeof input === 'number') {
        const fromNumber = new Date(input);
        return isValid(fromNumber) ? fromNumber : null;
    }
    return null;
}

export function formatDateTime(date) {
    const dateTimeObject = toValidDate(date);
    if (!dateTimeObject) {
        return '';
    }
    return format(dateTimeObject, `PP '${t('at')}' p`, {
        locale: getDateFnsCurrentLocale(i18n.language),
    });
}

export function formatDateTimeShort(date) {
    const dateTimeObject = toValidDate(date);
    if (!dateTimeObject) {
        return '';
    }
    return format(dateTimeObject, 'Pp', { locale: getDateFnsCurrentLocale(i18n.language) });
}

export function formatDate(date) {
    const dateTimeObject = toValidDate(date);
    if (!dateTimeObject) {
        return '';
    }

    return format(dateTimeObject, 'PP', {
        locale: getDateFnsCurrentLocale(i18n.language),
    });
}

export function formatTime(date) {
    const dateTimeObject = toValidDate(date);
    if (!dateTimeObject) {
        return '';
    }

    return format(dateTimeObject, 'p', {
        locale: getDateFnsCurrentLocale(i18n.language),
    });
}

export function formatDateTimeFromRange(isRangeDefined, date) {
    return isRangeDefined && date ? formatDateTime(date) : '';
}

export function formatDateTimeRange(start, end) {
    const startDate = toValidDate(start);
    const endDate = toValidDate(end);
    if (!startDate) return '-';

    if (!endDate) return formatDateTime(startDate);

    if (startDate.toDateString() === endDate.toDateString()) {
        // Show date once and time range
        const locale = getDateFnsCurrentLocale(i18n.language);
        return format(startDate, 'PPp', { locale }).replace(
            format(startDate, 'p', { locale }),
            `${format(startDate, 'p', { locale })} - ${format(endDate, 'p', { locale })}`
        );
    }

    return `${formatDateTime(startDate)} - ${formatDateTime(endDate)}`;
}

export function formatRelativeDate(date) {
    const dateObject = toValidDate(date);
    if (!dateObject) {
        return '';
    }

    const locale = getDateFnsCurrentLocale(i18n.language);

    if (isToday(dateObject)) {
        const minutesDiff = Math.abs(Math.floor((Date.now() - dateObject.getTime()) / 60000));
        if (minutesDiff < 60) {
            return `${Math.max(minutesDiff, 1)}min.`;
        }

        const hoursDiff = Math.max(Math.floor(minutesDiff / 60), 1);
        return `${hoursDiff}h.`;
    }

    if (isYesterday(dateObject)) {
        return t('Yesterday');
    }

    const daysDiff = Math.abs(Math.floor((Date.now() - dateObject.getTime()) / 86400000));
    if (daysDiff <= 7) {
        return `${Math.max(daysDiff, 1)}j`;
    }

    if (isSameYear(dateObject, new Date())) {
        return format(dateObject, 'dd MMM', { locale });
    }

    return format(dateObject, 'dd/MM/yy', { locale });
}

export function formatDateTimeRangeForOrder(datetimeRange, formatter = formatDateTime) {
    if (!datetimeRange) {
        return t('Not specified');
    }
    const startDatetime = toValidDate(datetimeRange?.start?.value ?? datetimeRange?.start);
    const endDatetime = toValidDate(datetimeRange?.end?.value ?? datetimeRange?.end);

    if (startDatetime && endDatetime) {
        const startHHmm = format(startDatetime, 'HH:mm');
        const endHHmm = format(endDatetime, 'HH:mm');
        if (startHHmm === '08:00' && endHHmm === '12:00') {
            return `${formatDate(startDatetime)} - ${t('Morning')}`;
        }
        if (startHHmm === '14:00' && endHHmm === '18:00') {
            return `${formatDate(startDatetime)} - ${t('Afternoon')}`;
        }
    }
    return startDatetime ? formatter(startDatetime) : '';
}

export function formatDateToLocaleForExport(date) {
    const dateTimeObject = toValidDate(date);
    if (!dateTimeObject) {
        return '';
    }

    return format(dateTimeObject, 'yyyy-MM-dd HH:mm:ss');
}

export const formatOpeningHours = (openingHours, userTimeZone) => {
    if (!Array.isArray(openingHours)) {
        return [];
    }
    return openingHours
        .filter(
            (entry) =>
                entry != null &&
                Number.isFinite(entry.hour) &&
                Number.isFinite(entry.minute) &&
                typeof entry.timezone === 'string' &&
                entry.timezone !== ''
        )
        .map(({ hour, minute, timezone }) => {
            const now = new Date();
            const localDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, minute, 0);
            const utcDate = zonedTimeToUtc(localDate, timezone);
            const userDate = userTimeZone ? utcToZonedTime(utcDate, userTimeZone) : utcDate;
            return format(userDate, 'HH:mm', userTimeZone ? { timeZone: userTimeZone } : undefined);
        });
};
